// FirebaseService.js
import { httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { functions } from '../firebase';

class FirebaseService {
    static instance = new FirebaseService();

    constructor() {
        this.functions = functions;
        this.cache = new Map();

        // Only connect to emulator in development
        if (window.location.hostname === 'localhost') {
            connectFunctionsEmulator(this.functions, 'localhost', 5001);
        }
    }

    // ============================================
    // Multi-step MEL Generation Methods
    // ============================================

    /**
     * Step 1: Generate model proposals for a topic
     */
    async generateModels(topic, modelCount = 3, existingModels = []) {
        try {
            const fn = httpsCallable(this.functions, 'generateModels');
            const result = await fn({ topic, modelCount, existingModels });
            return result.data.models;
        } catch (error) {
            console.error('Model generation error:', error);
            throw new Error(error.message || 'Failed to generate models');
        }
    }

    /**
     * Step 2: Generate evidence proposals for approved models
     */
    async generateEvidences(topic, approvedModels, evidenceCount = 6, existingEvidences = []) {
        try {
            const fn = httpsCallable(this.functions, 'generateEvidences');
            const result = await fn({ topic, approvedModels, evidenceCount, existingEvidences });
            return result.data.evidences;
        } catch (error) {
            console.error('Evidence generation error:', error);
            throw new Error(error.message || 'Failed to generate evidences');
        }
    }

    /**
     * Step 3: Generate references for evidences
     */
    async generateReferences(evidences) {
        try {
            const fn = httpsCallable(this.functions, 'generateReferences');
            const result = await fn({ evidences });
            return result.data.evidences;
        } catch (error) {
            console.error('Reference generation error:', error);
            throw new Error(error.message || 'Failed to generate references');
        }
    }

    /**
     * Step 4: Validate references (check URL accessibility)
     */
    async validateReferences(evidences) {
        try {
            const fn = httpsCallable(this.functions, 'validateReferences');
            const result = await fn({ evidences });
            return result.data.evidences;
        } catch (error) {
            console.error('Reference validation error:', error);
            throw new Error(error.message || 'Failed to validate references');
        }
    }

    /**
     * Step 5: Assemble final MEL with links
     */
    async assembleMEL(title, topic, models, evidences) {
        try {
            const fn = httpsCallable(this.functions, 'assembleMEL');
            const result = await fn({ title, topic, models, evidences });
            return result.data;
        } catch (error) {
            console.error('MEL assembly error:', error);
            throw new Error(error.message || 'Failed to assemble MEL');
        }
    }

    async resetStudentPassword(studentEmail, newPassword) {
        try {
            const fn = httpsCallable(this.functions, 'resetStudentPassword');
            const result = await fn({ studentEmail, newPassword });
            return result.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to reset password');
        }
    }

    async translateText(text, targetLang, timeout = 10000) {

        if (!text || targetLang === 'en') return text;
        const cacheKey = `${text}:${targetLang}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            const translateTextWithCors = httpsCallable(
                this.functions,
                'translateTextWithCors'
            );

            // Create a promise that rejects after timeout
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Translation timeout')), timeout)
            );

            // Race between the translation and timeout
            const result = await Promise.race([
                translateTextWithCors({
                    text,
                    targetLanguage: targetLang,
                }),
                timeoutPromise
            ]);

            // Updated validation logic to match the actual response structure
            if (!result || !result.data || !result.data.text) {
                console.error('Invalid translation response structure:', result);
                throw new Error('Invalid translation response structure');
            }

            const translatedText = result.data.text;
            this.cache.set(cacheKey, translatedText);

            return translatedText;
        } catch (error) {
            console.error('Translation error:', {
                error,
                text: text.substring(0, 50),
                targetLang,
            });

            // Return original text on error
            return text;
        }
    }
}

export const firebaseService = FirebaseService.instance;